import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import {
	ArticleCardLink,
	ArticleImage,
	ContentErrorState,
} from "@/components/blocks/editorial";
import MarkdownContent from "@/components/blocks/MarkdownContent";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
	getAgentContentUrls,
	getAgentReadableRedirectPath,
	getCanonicalPageUrl,
} from "@/lib/ai-content";
import { useContentReadTracking } from "@/lib/analytics";
import { getClosestArticlesByDate } from "@/lib/blog";
import { getBlogSeoOverride } from "@/lib/blog-seo";
import { content } from "@/lib/content";
import { renderContentMarkdown } from "@/lib/content-markdown";
import { getTranslator } from "@/lib/i18n";
import { buildPath, resolveLocaleForPath } from "@/lib/i18n/locale";
import { useI18n } from "@/lib/i18n/use-i18n";
import { requirePublicContent } from "@/lib/public-content";
import {
	buildArticleStructuredData,
	buildBreadcrumbStructuredData,
	buildFaqStructuredData,
	buildSeo,
	type StructuredData,
	stripHtml,
	toExcerpt,
} from "@/lib/seo";

export const Route = createFileRoute("/{-$locale}/blog/$slug")({
	component: BlogDetailPage,
	loader: async ({ params, location, serverContext }) => {
		const locale = resolveLocaleForPath(location.pathname, serverContext);
		const agentReadableRedirectPath = getAgentReadableRedirectPath(
			location.pathname,
			locale,
		);
		if (agentReadableRedirectPath) {
			throw redirect({
				href: buildPath(
					agentReadableRedirectPath,
					location.searchStr,
					location.hash,
				),
			});
		}

		const [result, posts] = await Promise.all([
			content.getPostDetails({ data: { slug: params.slug, locale } }),
			content.getPosts({ data: { locale } }),
		]);

		const { content: post, availableLocales } = requirePublicContent(
			result,
			`/blog/${params.slug}`,
			location.searchStr,
			location.hash,
		);

		const contentHtml = renderContentMarkdown(post.content, post.media);

		return { post, contentHtml, posts, locale, availableLocales };
	},
	head: ({ loaderData }) => {
		if (!loaderData) return {};
		const t = getTranslator(loaderData.locale);
		const descriptionFallback = t((t) => t.blog.description);
		const contentText = stripHtml(loaderData.contentHtml);
		const seoOverride = getBlogSeoOverride(
			loaderData.post.slug,
			loaderData.locale,
		);
		const title = seoOverride?.title ?? loaderData.post.title;
		const description =
			seoOverride?.description ??
			(contentText ? toExcerpt(contentText) : descriptionFallback);
		const path = `/blog/${loaderData.post.slug}`;
		const structuredData: StructuredData[] = [
			buildArticleStructuredData({
				title,
				description,
				path,
				locale: loaderData.locale,
				imageUrl: loaderData.post.illustration?.socialSrc,
				publishedAt: loaderData.post.publishedAt,
				modifiedAt: loaderData.post.updatedAt,
			}),
			buildBreadcrumbStructuredData([
				{
					name: t((t) => t.nav.home),
					url: getCanonicalPageUrl("/", loaderData.locale),
				},
				{
					name: t((t) => t.nav.blog),
					url: getCanonicalPageUrl("/blog", loaderData.locale),
				},
				{
					name: title,
					url: getCanonicalPageUrl(path, loaderData.locale),
				},
			]),
		];

		if (seoOverride?.faq.length) {
			structuredData.push(buildFaqStructuredData(seoOverride.faq));
		}

		return buildSeo({
			availableLocales: loaderData.availableLocales,
			title,
			description,
			path,
			locale: loaderData.locale,
			imageUrl: loaderData.post.illustration?.socialSrc,
			type: "article",
			agentReadable: getAgentContentUrls(path, loaderData.locale),
			structuredData,
		});
	},
	pendingComponent: () => (
		<div className="mx-auto w-full max-w-3xl space-y-8 px-6 py-16">
			<Skeleton className="mx-auto h-10 w-3/4" />
			<Skeleton className="aspect-video w-full" />
			<div className="space-y-4">
				<Skeleton className="h-4 w-full" />
				<Skeleton className="h-4 w-full" />
				<Skeleton className="h-4 w-3/4" />
			</div>
		</div>
	),
	errorComponent: ({ error }) => <BlogPostError error={error} />,
});

function BlogDetailPage() {
	const { post, contentHtml, posts } = Route.useLoaderData();
	const { t, locale, localeParam } = useI18n();
	const localeParams: Record<string, string> = localeParam
		? { locale: localeParam }
		: {};
	const suggestedArticles = getClosestArticlesByDate(post, posts, 3);
	const seoOverride = getBlogSeoOverride(post.slug, locale);
	const displayTitle = seoOverride?.title ?? post.title;

	useContentReadTracking({
		contentType: "blog",
		slug: post.slug,
		title: displayTitle,
		locale,
	});

	return (
		<article className="mx-auto w-full max-w-3xl px-6 py-12 md:py-16">
			<Button variant="ghost" size="sm" asChild className="-ml-3">
				<Link to="/{-$locale}/blog" params={localeParams}>
					<ArrowLeft />
					{t((t) => t.blog.back)}
				</Link>
			</Button>
			<header className="mt-8 space-y-4 text-center">
				<h1 className="text-3xl font-bold tracking-tight text-balance md:text-5xl">
					{displayTitle}
				</h1>
				<p className="text-sm text-muted-foreground">{post.publishedAtLabel}</p>
			</header>
			<div className="mt-10 aspect-video w-full overflow-hidden rounded-xl border">
				<ArticleImage
					image={post.illustration}
					sizes="(min-width: 816px) 768px, calc(100vw - 48px)"
					fetchPriority="high"
					loading="eager"
					alt={displayTitle}
					className="h-full w-full object-cover"
				/>
			</div>
			<MarkdownContent contentHtml={contentHtml} className="mt-10" />
			{seoOverride ? <SeoInsightSection override={seoOverride} /> : null}

			{suggestedArticles.length > 0 ? (
				<>
					<Separator className="my-16" />
					<h2 className="mb-8 text-center text-2xl font-semibold tracking-tight">
						{t((t) => t.blog.other)}
					</h2>
					<div className="grid grid-cols-1 gap-6 md:grid-cols-3">
						{suggestedArticles.map((article) => (
							<ArticleCardLink
								key={article.slug}
								article={article}
								localeParams={localeParams}
								placement="blog_suggested"
								variant="compact"
							/>
						))}
					</div>
				</>
			) : null}
		</article>
	);
}

function SeoInsightSection({
	override,
}: {
	override: NonNullable<ReturnType<typeof getBlogSeoOverride>>;
}) {
	const { locale } = useI18n();
	const labels =
		locale === "fr-FR"
			? { takeaways: "À retenir", faq: "FAQ rapide" }
			: { takeaways: "Key takeaways", faq: "Quick FAQ" };

	return (
		<Card className="mt-12">
			<CardHeader>
				<CardTitle>{labels.takeaways}</CardTitle>
			</CardHeader>
			<CardContent className="space-y-6 text-sm text-muted-foreground">
				<p>{override.summary}</p>
				<ul className="list-disc space-y-2 pl-5">
					{override.keyPoints.map((point) => (
						<li key={point}>{point}</li>
					))}
				</ul>
				<div className="space-y-4">
					<h2 className="text-lg font-semibold text-foreground">
						{labels.faq}
					</h2>
					{override.faq.map((item) => (
						<div key={item.question} className="border-t pt-4">
							<h3 className="font-medium text-foreground">{item.question}</h3>
							<p className="mt-2">{item.answer}</p>
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	);
}

function BlogPostError({ error }: { error: Error }) {
	const { t } = useI18n();

	return (
		<ContentErrorState
			error={error}
			title={t((t) => t.blog.errorTitleDetail)}
			fallback={t((t) => t.blog.errorFallback)}
			source="blog_detail"
		/>
	);
}
